import { Button } from '@tremor/react';
import clsx from 'clsx';
import { UseFormReturn } from 'react-hook-form';
import type { LoginFormValues } from '../firebaseAuthSchemas';
import type { ResetMessageState } from '../firebaseAuthTypes';
import {
  authTremorButtonNoFocusRing,
  authTremorPrimarySubmitClass,
} from '../authFormTremor';
import { AuthTextField } from './AuthTextField';
import { AuthPasswordField } from './AuthPasswordField';

interface AuthLoginFormProps {
  readonly form: UseFormReturn<LoginFormValues>;
  readonly formDisabled: boolean;
  readonly resetMessage: ResetMessageState;
  readonly showPassword: boolean;
  readonly onTogglePassword: () => void;
  readonly onSubmit: (data: LoginFormValues) => Promise<void>;
  readonly onForgotPassword: () => Promise<void>;
}

export function AuthLoginForm({
  form,
  formDisabled,
  resetMessage,
  showPassword,
  onTogglePassword,
  onSubmit,
  onForgotPassword,
}: AuthLoginFormProps) {
  const { register, formState, handleSubmit } = form;

  return (
    <form className="space-y-3" onSubmit={handleSubmit(onSubmit)} noValidate>
      <AuthTextField
        id="login-email"
        label="Email"
        type="email"
        autoComplete="email"
        disabled={formDisabled}
        error={formState.errors.email?.message}
        registration={register('email')}
      />
      <div>
        <AuthPasswordField
          id="login-password"
          label="Password"
          autoComplete="current-password"
          disabled={formDisabled}
          showPassword={showPassword}
          onToggleShow={onTogglePassword}
          error={formState.errors.password?.message}
          registration={register('password')}
        />
        <div className="mt-1.5 flex justify-end">
          <Button
            type="button"
            variant="light"
            color="rose"
            onClick={onForgotPassword}
            disabled={formDisabled || resetMessage === 'sending'}
            className={clsx(
              '!h-auto !rounded-md !border-0 !bg-transparent !p-0 !shadow-none hover:!bg-transparent hover:underline disabled:!opacity-50',
              authTremorButtonNoFocusRing,
            )}
          >
            <span className="text-sm font-medium text-primary-600">
              {resetMessage === 'sending' ? 'Sending…' : 'Forgot password?'}
            </span>
          </Button>
        </div>
      </div>
      <Button
        type="submit"
        size="sm"
        color="rose"
        disabled={formDisabled}
        loading={formDisabled}
        loadingText="Signing in…"
        className={clsx(authTremorPrimarySubmitClass, '!py-2.5')}
      >
        Sign in
      </Button>
    </form>
  );
}
